import { useEffect, useState, type FormEvent } from "react";
import { Icon } from "../components/Icon";
import { Modal } from "../components/Modal";
import type { LatestPlanSnapshot, ReminderSettings, TokenResponse, User } from "../types";
import { readReminderSettings, writeReminderSettings } from "../utils";

type InfoKind = "privacy" | "medical" | "password" | "reminder";

interface SettingsPageProps {
  user: User;
  snapshot: LatestPlanSnapshot | null;
  updateName: (name: string) => Promise<User>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<TokenResponse>;
  revokeSessions: () => Promise<TokenResponse>;
  deleteAccount: (password: string) => Promise<void>;
  onToken: (token: string) => void;
  onDeleted: () => void;
  onEditProfile: () => void;
  confirmRevoke: () => Promise<boolean>;
  onError: (message: string) => void;
}

export function SettingsPage({ user, snapshot, updateName, changePassword, revokeSessions, deleteAccount, onToken, onDeleted, onEditProfile, confirmRevoke, onError }: SettingsPageProps) {
  const [name, setName] = useState(user.name);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [reminder, setReminder] = useState<ReminderSettings>(readReminderSettings);
  const [info, setInfo] = useState<InfoKind | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => setName(user.name), [user.name]);

  async function saveName(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy("name");
    try { await updateName(name.trim()); } catch (caught) { onError(message(caught)); } finally { setBusy(""); }
  }

  async function savePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy("password");
    try {
      const response = await changePassword(currentPassword, newPassword);
      onToken(response.access_token);
      setCurrentPassword("");
      setNewPassword("");
      setInfo("password");
    } catch (caught) { onError(message(caught)); } finally { setBusy(""); }
  }

  async function revoke() {
    if (!(await confirmRevoke())) return;
    setBusy("revoke");
    try {
      const response = await revokeSessions();
      onToken(response.access_token);
    } catch (caught) { onError(message(caught)); } finally { setBusy(""); }
  }

  async function saveReminder(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    let enabled = reminder.enabled;
    if (enabled && !("Notification" in window)) enabled = false;
    if (enabled && Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      enabled = permission === "granted";
    }
    const next = { ...reminder, enabled, lastDate: "" };
    writeReminderSettings(next);
    setReminder(next);
    setInfo("reminder");
  }

  async function removeAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy("delete");
    try {
      await deleteAccount(deletePassword);
      onDeleted();
    } catch (caught) { onError(message(caught)); } finally { setBusy(""); }
  }

  return (
    <section className="route active-route">
      <header className="page-header"><div><p className="eyebrow">ACCOUNT</p><h1>Settings</h1></div></header>
      <div className="settings-grid">
        <section className="card"><div className="section-title"><div><p className="eyebrow">PROFILE</p><h2>Account details</h2></div></div><form className="form-stack" onSubmit={saveName}><label className="field">Name<input required maxLength={255} value={name} onChange={(event) => setName(event.target.value)} /></label><label className="field">Email<input disabled value={user.email} /></label><button className="button primary" type="submit" disabled={busy === "name"}>{busy === "name" ? "Saving…" : "Save profile"}</button></form></section>

        <section className="card"><div className="section-title"><div><p className="eyebrow">PLAN</p><h2>Training preferences</h2></div></div><dl><div><dt>Status</dt><dd>{snapshot ? "Active" : "Not created"}</dd></div><div><dt>Membership</dt><dd>{user.is_premium ? "FitTracker Pro" : "Free"}</dd></div><div><dt>Goals</dt><dd>{snapshot?.profile.goals.join(", ") || "Not set"}</dd></div></dl><button className="button secondary full" type="button" onClick={onEditProfile}>Edit goals and constraints</button></section>

        <section className="card"><div className="section-title"><div><p className="eyebrow">REMINDERS</p><h2>Browser reminder</h2></div></div><p className="muted">Shown while FitTracker is open. Calendar export remains the reliable option when the browser is closed.</p><form className="inline-form" onSubmit={saveReminder}><label className="field grow">Time<input type="time" value={reminder.time} onChange={(event) => setReminder({ ...reminder, time: event.target.value })} /></label><label className="switch-row"><input type="checkbox" checked={reminder.enabled} onChange={(event) => setReminder({ ...reminder, enabled: event.target.checked })} /><span>Enabled</span></label><button className="button secondary" type="submit">Save</button></form></section>

        <section className="card"><div className="section-title"><div><p className="eyebrow">SECURITY</p><h2>Password and sessions</h2></div></div><form className="form-stack" onSubmit={savePassword}><label className="field">Current password<input type="password" autoComplete="current-password" required value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} /></label><label className="field">New password<input type="password" autoComplete="new-password" minLength={8} required value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label><button className="button secondary" type="submit" disabled={busy === "password"}>{busy === "password" ? "Updating…" : "Change password"}</button></form><button className="button text danger-text" type="button" disabled={busy === "revoke"} onClick={() => void revoke()}>{busy === "revoke" ? "Revoking…" : "Sign out other sessions"}</button></section>

        <section className="card"><div className="section-title"><div><p className="eyebrow">PRIVACY</p><h2>Data and safety</h2></div></div><div className="button-stack"><button className="button secondary" type="button" onClick={() => setInfo("privacy")}><Icon name="lock" />Privacy & data</button><button className="button secondary" type="button" onClick={() => setInfo("medical")}><Icon name="info" />Medical disclaimer</button></div></section>

        <section className="card danger-zone"><div className="section-title"><div><p className="eyebrow danger-copy">DANGER ZONE</p><h2>Delete account</h2></div></div><p className="muted">Permanently deletes plans, workouts, meals, and connected-service credentials.</p><button className="button danger" type="button" onClick={() => setDeleteOpen(true)}><Icon name="trash" />Delete account</button></section>
      </div>

      <Modal open={info !== null} title={infoTitle(info)} eyebrow={info === "medical" ? "SAFETY" : info === "password" ? "SECURITY" : info === "reminder" ? "REMINDERS" : "PRIVACY"} onClose={() => setInfo(null)} footer={<button className="button primary" type="button" onClick={() => setInfo(null)}>Done</button>}>
        {info === "privacy" ? <div className="prose"><h3>Account data</h3><p>Plans, synchronized workouts, web meal logs, and connected-service credentials are stored on the FitTracker backend for your account.</p><h3>Health data</h3><p>Recovery summaries support plan adaptation. Raw Apple Health data remains controlled by the mobile app and Apple permissions.</p><h3>Your controls</h3><p>You can disconnect services or permanently delete the account from this page.</p></div> : null}
        {info === "medical" ? <div className="prose"><p>FitTracker provides general fitness and nutrition guidance and is not medical advice, diagnosis, or treatment.</p><p>Stop exercising and seek professional care for pain, dizziness, unusual shortness of breath, or other concerning symptoms.</p></div> : null}
        {info === "password" ? <p className="muted">Your password was updated. Previous sessions were invalidated and this browser received a replacement token.</p> : null}
        {info === "reminder" ? <p className="muted">The reminder is saved for this browser. It runs while FitTracker is open; use calendar export for background reminders.</p> : null}
      </Modal>

      <Modal open={deleteOpen} title="Permanently delete your account?" eyebrow="DANGER ZONE" onClose={() => setDeleteOpen(false)}>
        <form className="form-stack" onSubmit={removeAccount}><p className="muted">This cannot be undone. Plans, workouts, meals, and connected services will be removed.</p><label className="field">Password<input type="password" autoComplete="current-password" value={deletePassword} onChange={(event) => setDeletePassword(event.target.value)} /></label><footer className="modal-footer"><button className="button text" type="button" onClick={() => setDeleteOpen(false)}>Cancel</button><button className="button danger" type="submit" disabled={busy === "delete"}>{busy === "delete" ? "Deleting…" : "Delete account"}</button></footer></form>
      </Modal>
    </section>
  );
}

function infoTitle(info: InfoKind | null): string {
  if (info === "medical") return "Medical disclaimer";
  if (info === "password") return "Password updated";
  if (info === "reminder") return "Reminder saved";
  return "Privacy & data";
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : "The operation failed.";
}
